import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DollarSign, User, AlertCircle, MessageSquare, History, CheckCircle2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';
import { useAuth } from '../contexts/AuthContext';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function AccountsReceivablePage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [openPayment, setOpenPayment] = useState(false);
  
  // History dialog state
  const [historyAccount, setHistoryAccount] = useState(null);
  const [openHistory, setOpenHistory] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customer-accounts`);
      const filteredAccounts = response.data.filter(acc => acc.balance > 0);
      setAccounts(filteredAccounts);
    } catch (error) {
      toast.error('Error al cargar cuentas por cobrar');
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedAccount || !paymentAmount) return;

    try {
      await axios.post(`${API}/customer-accounts/${selectedAccount.customer_id}/payment`, {
        amount: Math.round(parseFloat(paymentAmount)),
        payment_date: new Date().toISOString(),
        payment_method: paymentMethod,
        notes: paymentNotes || null
      });
      toast.success('Pago registrado exitosamente');
      setOpenPayment(false);
      setPaymentAmount('');
      setPaymentMethod('efectivo');
      setPaymentNotes('');
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  const openPaymentDialog = (account) => {
    setSelectedAccount(account);
    setPaymentAmount(account.balance.toString());
    setOpenPayment(true);
  };

  const openHistoryDialog = (account) => {
    setHistoryAccount(account);
    setOpenHistory(true);
  };

  const handleWhatsAppSend = (account) => {
    const customer = account.customer;
    if (!customer?.phone) {
      toast.error('El cliente no tiene teléfono registrado para WhatsApp.');
      return;
    }

    const cleanPhone = customer.phone.replace(/[^\d]/g, '');
    const clientName = customer.name || account.customer_name;
    const companyName = user?.company_name || 'GKService';
    
    // Construct Chile standard phone code if missing
    let finalPhone = cleanPhone;
    if (cleanPhone.length === 9) {
      finalPhone = '56' + cleanPhone; // Chilean mobile prefix
    }
    
    const message = encodeURIComponent(
      `Hola ${clientName}, te saludamos de ${companyName}. Queremos recordarte amigablemente que tienes un saldo pendiente de ${formatCLP(account.balance)} en tu cuenta de crédito. Puedes realizar transferencia o pagar directamente en local. ¡Muchas gracias!`
    );

    window.open(`https://wa.me/${finalPhone}?text=${message}`, '_blank');
  };

  const totalReceivable = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="p-3 bg-indigo-600 rounded-2xl shadow-md">
          <DollarSign className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Cuentas por Cobrar</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los saldos pendientes de clientes y envía recordatorios automáticos
          </p>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 border-indigo-200 dark:border-indigo-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total por Cobrar (Consolidado)</p>
              <h2 className="text-4xl font-bold font-mono text-indigo-600 dark:text-indigo-400">
                {formatCLP(totalReceivable)}
              </h2>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-1">Clientes Deudores</p>
              <p className="text-3xl font-bold">{accounts.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <div className="space-y-4">
        {accounts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 opacity-50 text-emerald-500" />
              <p>No hay cuentas por cobrar pendientes. ¡Todo al día!</p>
            </CardContent>
          </Card>
        ) : (
          accounts.map((account) => {
            const clientName = account.customer?.name || account.customer_name || 'Cliente desconocido';
            const clientRut = account.customer?.rut || 'Sin RUT';
            const clientPhone = account.customer?.phone || 'Sin Teléfono';

            return (
              <Card
                key={account.id}
                className="hover:shadow-md transition-shadow"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                          <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{clientName}</h3>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono">
                            <span>RUT: {clientRut}</span>
                            <span>Teléfono: {clientPhone}</span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 border-t pt-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Saldo Pendiente</p>
                          <p className="text-lg font-bold font-mono text-red-600 dark:text-red-400">
                            {formatCLP(account.balance)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Total Transacciones</p>
                          <p className="text-lg font-semibold">{account.transactions?.length || 0}</p>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <p className="text-xs text-muted-foreground">Última Actividad</p>
                          <p className="text-sm font-medium">
                            {account.last_payment_date
                              ? new Date(account.last_payment_date).toLocaleDateString('es-CL')
                              : 'Sin pagos registrados'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 w-full lg:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openHistoryDialog(account)}
                        className="flex-1 lg:flex-none border-indigo-200 hover:bg-indigo-50 dark:border-indigo-800 dark:hover:bg-indigo-950"
                      >
                        <History className="h-4 w-4 mr-2 text-indigo-600" />
                        Historial
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleWhatsAppSend(account)}
                        className="flex-1 lg:flex-none border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-800 dark:hover:bg-emerald-950"
                        disabled={!account.customer?.phone}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        WhatsApp
                      </Button>
                      <Button
                        onClick={() => openPaymentDialog(account)}
                        className="flex-1 lg:flex-none shadow-md bg-indigo-600 hover:bg-indigo-700 text-white"
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Registrar Pago
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* History Dialog */}
      <Dialog open={openHistory} onOpenChange={setOpenHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historial de Cuenta: {historyAccount?.customer?.name || historyAccount?.customer_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
            <div className="grid grid-cols-2 gap-4 bg-muted p-3 rounded-lg">
              <div>
                <span className="text-xs text-muted-foreground block">Deuda / Saldo Actual</span>
                <span className="text-xl font-bold font-mono text-red-600">
                  {formatCLP(historyAccount?.balance || 0)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Último Pago</span>
                <span className="text-xl font-bold">
                  {historyAccount?.last_payment_date
                    ? new Date(historyAccount.last_payment_date).toLocaleDateString('es-CL')
                    : 'Ninguno'}
                </span>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted text-muted-foreground text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Tipo</th>
                    <th className="px-4 py-2">Detalles</th>
                    <th className="px-4 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {historyAccount?.transactions && historyAccount.transactions.map((trans, idx) => (
                    <tr key={idx} className="hover:bg-muted/50">
                      <td className="px-4 py-2 whitespace-nowrap">
                        {new Date(trans.date).toLocaleDateString('es-CL')}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                          trans.type === 'payment' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {trans.type === 'payment' ? 'Abono / Pago' : 'Crédito / Compra'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs truncate max-w-[200px]">
                        {trans.payment_method ? `Método: ${trans.payment_method}` : `ID Venta: ${trans.sale_id?.slice(0, 8)}`}
                        {trans.notes && <span className="block text-muted-foreground italic">{trans.notes}</span>}
                      </td>
                      <td className={`px-4 py-2 text-right font-mono font-bold ${trans.type === 'payment' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {trans.type === 'payment' ? '-' : '+'}{formatCLP(trans.amount)}
                      </td>
                    </tr>
                  ))}
                  {(!historyAccount?.transactions || historyAccount.transactions.length === 0) && (
                    <tr>
                      <td colSpan="4" className="text-center py-6 text-muted-foreground">No hay transacciones registradas</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={openPayment} onOpenChange={setOpenPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pago / Abono</DialogTitle>
          </DialogHeader>
          {selectedAccount && (
            <form onSubmit={handlePayment} className="space-y-4">
              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                <p className="font-semibold text-lg">
                  {selectedAccount.customer?.name || selectedAccount.customer_name}
                </p>
                <p className="text-xs text-muted-foreground mt-2 mb-1">Saldo Pendiente Actual</p>
                <p className="text-2xl font-bold font-mono text-red-600 dark:text-red-400">
                  {formatCLP(selectedAccount.balance)}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta debito">Tarjeta de Débito</option>
                  <option value="tarjeta credito">Tarjeta de Crédito</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Monto a Pagar (CLP)</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  onBlur={(e) => setPaymentAmount(Math.round(parseFloat(e.target.value || 0)).toString())}
                  required
                  min="1"
                  max={selectedAccount.balance}
                />
                <p className="text-xs text-muted-foreground">
                  Saldo después del pago: {formatCLP(selectedAccount.balance - Math.round(parseFloat(paymentAmount || 0)))}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Comentarios / Notas</Label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Ej. Abono parcial de deuda"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenPayment(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  Confirmar Pago
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}